import 'isomorphic-fetch';
import { getCacheItem, getCacheKey } from '../reducers/queryCache';
import { getQueryString, getQueryCacheKey } from '../reducers/query';
import { getCurrentLangCode } from '../reducers/i18n';

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
const callApi = async (endpoint, baseURI, formatter, query = '') => {
    const base = (endpoint.indexOf(baseURI) === -1) ? baseURI + endpoint : endpoint;
    const callURI = `${base}?${query}`;

    const response = await fetch(callURI);
    const result = await response.json();
    if (!response.ok) {
        return Promise.reject(result);
    }
    return formatter(result);
};

// Action key that carries API call info interpreted by this Redux middleware.
export const CALL_API = Symbol('Call API');

// A Redux middleware that interprets actions with CALL_API info specified.
// Performs the call and promises when such actions are dispatched.
export default store => next => action => {
    const callAPI = action[CALL_API];

    if (typeof callAPI === 'undefined') {
        return next(action);
    }

    const { query, types, definition } = callAPI;
    const { endpoint, formatter, baseURI, ttl } = definition;

    if (!Array.isArray(types) || types.length !== 4) {
        throw Error('Expected an array of four action types.');
    }
    if (!types.every(type => typeof type === 'string')) {
        throw Error('Expected action types to be strings.');
    }

    const currentState = store.getState();

    const cachedQuery = getCacheItem(currentState, Object.assign({}, query, { language: getCurrentLangCode(currentState) }));

    if (cachedQuery) {
        // Wether the cache is expired.
        if ((Date.now() - cachedQuery.fetched) < ttl) {
            return Promise.resolve();
        }
    }

    function actionWith(data) {
        const finalAction = Object.assign({}, action, data, { fetchedAt: Date.now() });
        delete finalAction[CALL_API];
        return finalAction;
    }

    const [queryType, requestType, successType, failureType] = types;
    next(actionWith({ type: queryType, query, endpoint }));

    // Fetch the state after the Query is set, to pass that down to the
    // api call.
    const newState = store.getState();

    const isFetching = newState.stolo.entities.patterns.isFetching;
    const newQuery = getQueryString(newState);
    const currentQuery = getQueryString(currentState);
    // If the query doesn't change and we are in the middle of a fetch, bailout.
    // This should prevent us from fetching the same stuff multiple times async.
    if (newQuery === currentQuery && isFetching) {
        return Promise.resolve();
    }

    next(actionWith({ type: requestType, query: newQuery }));

    return callApi(endpoint, baseURI, formatter, newQuery).then(
        response => next(actionWith({
            response,
            type: successType,
            query: newQuery,
            endpoint,
        })),
        error => {
            console.error('Request error: %s', error.error);
            return next(actionWith({
                type: failureType,
                error: error.error || 'Something bad happened',
                code: error.errorCode || 666,
                endpoint,
            }));
        },
    );
};



// WEBPACK FOOTER //
// ./src/middleware/patterndesign-api.js